import { FC, ReactNode } from "react";
import { Link, LinkProps } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ActionLinkProps extends LinkProps {
  variant?: "light" | "dark" | "gradientLight";
  children: ReactNode;
  className?: string;
  showIcon?: boolean;
  onHover?: boolean;
  hoverGradientLight?: boolean;
  hoverDark?: boolean;
  hoverWhite?: boolean;
  hoverWhiteNoBorder?: boolean;
  hoverDarkWithBorder?: boolean;
}

const ActionLink: FC<ActionLinkProps> = ({
  variant = "light",
  children,
  className,
  showIcon = true,
  onHover = false,
  hoverGradientLight = false,
  hoverDark = false,
  hoverWhite = false,
  hoverWhiteNoBorder = false,
  hoverDarkWithBorder = false,
  ...props
}) => {
  const baseStyles = `flex ${showIcon ? "justify-between gap-5" : "justify-center gap-2.5"} items-center rounded-full px-4.5 py-3.5 text-base font-normal transition-all duration-300 ease-in-out md:py-4 shrink-0`;

  const variants = {
    light: "bg-white text-dark hover:bg-white/90",
    dark: "bg-dark text-white hover:bg-dark/90",
    gradientLight: "bg-linear-gradient-light text-white",
  };

  const hoverMotionClasses = onHover
    ? "[filter:brightness(1)] transform-gpu hover:[filter:brightness(1.1)] active:scale-95"
    : "";

  const hoverGradientLightClasses = hoverGradientLight
    ? "relative isolate hover:text-white before:pointer-events-none before:content-[''] before:absolute before:-inset-[1px] before:rounded-[inherit] before:opacity-0 before:transition-opacity before:duration-300 before:ease-out before:[background-image:var(--gradient-brand-light)] before:z-0 hover:before:opacity-100"
    : "";

  const hoverDarkClasses = hoverDark ? "hover:bg-dark hover:text-white" : "";
  const hoverWhiteClasses = hoverWhite
    ? "border border-transparent hover:bg-white hover:text-ink hover:border-gray-400"
    : "";
  const hoverWhiteNoBorderClasses = hoverWhiteNoBorder
    ? "hover:bg-white hover:text-ink"
    : "";
  const hoverDarkWithBorderClasses = hoverDarkWithBorder
    ? "border border-transparent hover:bg-dark hover:text-white hover:border-white"
    : "";

  return (
    <Link
      className={cn(
        baseStyles,
        variants[variant],
        hoverMotionClasses,
        hoverGradientLightClasses,
        hoverDarkClasses,
        hoverWhiteClasses,
        hoverWhiteNoBorderClasses,
        hoverDarkWithBorderClasses,
        className,
      )}
      {...props}
    >
      <span className="relative z-10">{children}</span>
      {showIcon && (
        <span className="relative z-10">
          <ArrowRight size={18} />
        </span>
      )}
    </Link>
  );
};

export default ActionLink;
